<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import type { FormItem } from '@/entries/interact-form/types'
import RadioQuestion from './question-types/RadioQuestion.vue'
import CheckboxQuestion from './question-types/CheckboxQuestion.vue'
import SelectButtonQuestion from './question-types/SelectButtonQuestion.vue'
import UnsupportedQuestion from './question-types/UnsupportedQuestion.vue'

const props = defineProps<{
  question: FormItem | null
  isModal?: boolean // モーダル表示かどうかのフラグを追加
}>()

const emit = defineEmits<{
  'update:answer': [value: string | string[], isSelectButton?: boolean]
}>()

const titleRef = ref<HTMLElement>()
const needsPadding = ref(false)

/**
 * 子コンポーネントからの回答を受け取って親に伝える
 *
 * 質問タイプに応じた処理を行い、親コンポーネントに回答を通知します。
 * select_buttonタイプの場合は、そのフラグも一緒に送信します。
 *
 * @param {string | string[]} value - 更新する回答（文字列または文字列の配列）
 */
function handleAnswerUpdate(value: string | string[]) {
  // select_buttonタイプの場合は、そのフラグも一緒に送信
  emit('update:answer', value, props.question?.type === 'select_button')
}

/**
 * モバイル表示時にタイトルテキストがコンシルジュアイコンと被るかチェック
 */
function checkTextOverlap() {
  if (!titleRef.value || props.isModal) return

  // モバイル表示かどうかをチェック（767px以下）
  if (window.innerWidth > 767) {
    needsPadding.value = false
    return
  }

  // テキストの幅を計算
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  if (!context) return

  // フォントスタイルを設定（CSSと同じ値）
  context.font = '18px sans-serif'
  const textWidth = context.measureText(props.question?.question || '').width

  // 画面幅からパディングを差し引いた利用可能幅
  const availableWidth = window.innerWidth - 20 // 左右パディング10px * 2

  // コンシルジュアイコンの幅（80px）とマージン（10px）を考慮
  const iconArea = 100 // 80px + 10px margin

  // テキストがアイコンエリアと重なるかチェック
  if (textWidth > availableWidth - iconArea) {
    needsPadding.value = true
  } else {
    needsPadding.value = false
  }
}

onMounted(() => {
  nextTick(() => {
    checkTextOverlap()
    window.addEventListener('resize', checkTextOverlap)
  })
})

// ウィンドウサイズ変更時のリスナーをクリーンアップ
onUnmounted(() => {
  window.removeEventListener('resize', checkTextOverlap)
})
</script>

<template>
  <div v-if="question" class="question-area" :class="{ 'modal-question': isModal }">
    <div class="question-title-wrapper" :class="{
      'modal-question-title-wrapper': isModal,
      'has-icon-padding': !isModal && needsPadding
    }">
      <!-- モーダル表示時のみコンシェルジュ画像を表示 -->
      <img v-if="isModal" src="@/entries/interact-form/img/concierge.png" alt="コンシェルジュ"
        class="concierge-image" />
      <p ref="titleRef" class="question-title" :class="{
        'modal-question-title': isModal,
        'has-icon-padding': !isModal && needsPadding
      }">
        {{ question.question }}
      </p>
    </div>

    <!-- 質問タイプに応じたコンポーネントを表示 -->
    <div class="question-content">
      <component :is="question.type === 'radio_button'
        ? RadioQuestion
        : question.type === 'check_box'
          ? CheckboxQuestion
          : question.type === 'select_button'
            ? SelectButtonQuestion
            : UnsupportedQuestion
        " :question="question" @update:answer="handleAnswerUpdate" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.question-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start; // 上揃えに変更
  border-radius: 26px;
  padding: 20px;
  width: 100%;
  box-sizing: border-box;
}

// Block: question-content
.question-content {
  max-height: none; // 親要素によってコントロールするため削除
  padding: 0 18px 0 28px;
  overflow-y: visible; // 親要素でスクロールするため
  width: 95%;
  box-sizing: border-box;
}
// Block: question-title-wrapper
.question-title-wrapper {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 24px;
}

// Block: modal-question-title-wrapper
.modal-question-title-wrapper {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
}

// Block: concierge-image
.concierge-image {
  width: 80px;
  height: auto;
  margin-right: 28px;
}

// Block: question-title
.question-title {
  font-size: 36px;
  width: 100%;
  text-align: center;
}

// Block: modal-question-title
.modal-question-title {
  position: relative;
  border: 3px solid #36b4a8;
  font-size: 24px;
  font-weight: 700;
  padding: 24px 20px;
  border-radius: 20px;
  background-color: #fff;
  text-align: left;
  max-width: calc(100% - 110px); // コンシェルジュ画像の幅+マージンを考慮

  // 吹き出しの左側の曲線部分
  &:before {
    content: '';
    position: absolute;
    top: 20px; // 上からの位置調整
    left: -20px; // 左側に配置
    width: 20px;
    height: 30px;
    border-radius: 0 0 0 15px; // 左下だけ丸くする
    box-shadow: inset -1px -12px #36b4a8;
    z-index: 0;
  }

  // 吹き出しの内側の曲線部分（白い部分）
  &:after {
    content: '';
    position: absolute;
    top: 18px; // 上からの位置調整（beforeより少し下に）
    left: -15px; // 左側に配置（beforeより少し右に）
    width: 19px;
    height: 28px;
    border-radius: 0 0 0 10px; // 左下だけ丸くする
    box-shadow: 0px -12px 0 -7px #fff inset;
    z-index: 1;
  }
}

// スマホ向けのスタイル
@media screen and (max-width: 767px) {
  // Block: question-area
  .question-area {
    padding: 15px 10px;
    border-radius: 16px;
  }

  // Block: question-content
  .question-content {
    padding: 0 10px;
    width: 100%;
  }

  // Block: question-title-wrapper
  .question-title-wrapper {
    margin-bottom: 15px;
  }

  // Block: modal-question-title-wrapper
  .modal-question-title-wrapper {
    flex-direction: column; // スマホでは縦並びに
    align-items: center;
  }

  // Block: concierge-image
  .concierge-image {
    width: 50px;
    margin-right: 0;
    margin-bottom: 10px;
  }

  // Block: question-title
  .question-title {
    font-size: 18px;
    width: 95%;

    &.has-icon-padding {
      padding-left: 30px; // アイコン幅80px + マージン20px
      width: calc(95% - 100px);
      text-align: left;
    }
  }

  // Block: modal-question-title
  .modal-question-title {
    border-width: 2px;
    font-size: 16px;
    padding: 12px 15px;
    border-radius: 15px;
    max-width: 100%; // スマホでは幅いっぱいに

    // 吹き出しの形状を調整
    &:before {
      // スマホでは吹き出しの形状を上部に変更
      top: -15px;
      left: 50%;
      transform: translateX(-50%);
      width: 30px;
      height: 15px;
      border-radius: 15px 15px 0 0;
      box-shadow: inset 0 -12px #36b4a8;
    }

    &:after {
      // スマホでは吹き出しの形状を上部に変更
      top: -12px;
      left: 50%;
      transform: translateX(-50%);
      width: 26px;
      height: 12px;
      border-radius: 13px 13px 0 0;
      box-shadow: 0 -12px 0 -10px #fff inset;
    }
  }
}
</style>
